import { Children, useEffect, useRef } from 'react';

import {
  FLY_DURATION_MS,
  type FlyTargetKey,
  useFlyTargetContext,
} from '#src/contexts/flyTarget/index.tsx';
import { useViewTransitionId } from '#src/hooks/ui/useViewTransitionId.ts';

type Props = {
  destination: FlyTargetKey;
  children: React.ReactNode;
  durationMs?: number;
  staggerMs?: number;
};

export const FlyToTarget = ({
  destination,
  children,
  durationMs = FLY_DURATION_MS,
  staggerMs = 50,
}: Props) => {
  const context = useFlyTargetContext();
  const id = useViewTransitionId('mtt');
  const ref = useRef<HTMLDivElement>(null);

  const items = Children.toArray(children);
  const count = items.length;
  const names = items.map((_, index) => `${id}-${index}`);

  useEffect(() => {
    if (!context) return undefined;

    context.registerGroup({
      id,
      destination,
      names: Array.from({ length: count }, (_, index) => `${id}-${index}`),
      durationMs,
      staggerMs,
      element: ref.current,
    });

    return () => context.unregisterGroup(id);
  }, [context, id, destination, count, durationMs, staggerMs]);

  return (
    <div className="contents" ref={ref}>
      {items.map((child, index) => (
        <div
          key={names[index]}
          style={{ viewTransitionName: names[index] } as React.CSSProperties}
        >
          {child}
        </div>
      ))}
    </div>
  );
};
